import { useCallback, useEffect, useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import useLocalStorage from '@zenbusiness/application-commons-hooks/useLocalStorage';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Snackbar from '@zenbusiness/zui/Snackbar';

import {
  Carousel,
  CarouselSlide,
  ReferAFriendCard,
  SMSOptInCard,
  TaxAdviceCard,
  SquarespaceDomainsCard,
  FundboxCard
} from '#client/components/Carousel';
import { HomeSection } from '#client/components/HomeSection';
import { ENABLE_FUNDBOX_QUALIFIED_LIST_CONTROL } from '#client/constants/experimentsFlags';
import { useBusinessEntityInfo } from '#client/hooks/useBusinessEntityInfo';
import useNotificationPreferences from '#client/hooks/useNotificationPreferences';
import useNotificationPreferencesUpdate from '#client/hooks/useNotificationPreferencesUpdate';

const SMS_OPT_IN_CARD_DISMISSED = 'smsOptInCardDismissed';

export const LOCALIZED_TEXT = {
  optInError: 'dashboard.carousel.card.smsOptIn.snackbar.error'
};

function DashboardCarousel() {
  const { userUuid } = useIdentity();
  const { formatMessage: fm } = useIntl();
  const { entitlements } = useBusinessEntity();
  const isFundboxQualified = useExperimentFlag(ENABLE_FUNDBOX_QUALIFIED_LIST_CONTROL, false);
  const { businessEntityInfo, loading: isBusinessEntityInfoLoading } = useBusinessEntityInfo();

  const { notificationPreferences, hasUnknownError } = useNotificationPreferences(userUuid);
  const {
    saveNotificationPreferences,
    loading,
    hasError: optInError
  } = useNotificationPreferencesUpdate(userUuid);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [isCardDismissed, setIsCardDismissed] = useLocalStorage(
    SMS_OPT_IN_CARD_DISMISSED,
    false
  );

  useEffect(() => {
    if (optInError) {
      setShowSnackbar(true);
    }
  }, [optInError]);

  const handleSnackbarOnClose = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  const activeSlides = (() => {
    const showSMSCard = !(
      notificationPreferences?.smsOptIn
      || isCardDismissed
      || hasUnknownError
    );

    const slides = new Map();
    if (showSMSCard) {
      slides.set('sms_opt_in', <SMSOptInCard
        currentPhoneNumber={notificationPreferences?.smsPhoneNumber}
        handleCardDismissed={setIsCardDismissed}
        handleSaveNotificationPreferences={saveNotificationPreferences}
        isSavingPhoneNumber={loading}
      />);
    }
    slides.set('tax_advice', <TaxAdviceCard />);
    slides.set('refer_a_friend', <ReferAFriendCard />);

    // Show Squarespace domains card if user has not purchased OpenSRS domains yet
    if (!entitlements.has('domain_name_reg')) {
      slides.set('squarespace_domains', <SquarespaceDomainsCard />);
    }

    // Show Fundbox card only for qualified users with businesses older than 6 months
    if (isFundboxQualified && !isBusinessEntityInfoLoading && businessEntityInfo?.formationDate) {
      const formationDate = new Date(businessEntityInfo.formationDate);
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      if (formationDate < sixMonthsAgo) {
        slides.set('fundbox', <FundboxCard />);
      }
    }

    return slides;
  })();

  const carouselEventingProperties = {
    bannerType: 'promo',
    elementName: 'dashboard_carousel',
    elementValue: Array.from(activeSlides.keys())
  };

  return (
    <HomeSection>
      <Carousel
        autoplayDurationInSeconds={15}
        totalSlides={activeSlides.size}
        eventingProperties={carouselEventingProperties}
      >
        {Array.from(activeSlides.entries()).map(([key, slide], index) => (
          <CarouselSlide
            key={key}
            slideIndex={index}
            totalSlides={activeSlides.size}
          >
            {slide}
          </CarouselSlide>
        ))}
      </Carousel>
      <Snackbar
        autoHideDuration={5000}
        onClose={handleSnackbarOnClose}
        open={showSnackbar}
        title={fm({ id: LOCALIZED_TEXT.optInError })}
        variant="alert"
      />
    </HomeSection>
  );
}

export default DashboardCarousel;
